import type { EstimateOption } from "../interfaces/payout";
import { PayoutMethodType } from "../interfaces/payout";
import type {
  IBankAccountPaymentMethod,
  ICardPaymentMethod,
} from "../interfaces/payment-method";
import type { ITransaction } from "../interfaces/transactions";
import { TransactionsStatus } from "../utils/commons";
import type { FundRequestMockReceipt } from "./fundRequestMock";

export const PAYOUT_CLAIM_MOCK_FX = 17.42;

export const payoutClaimMockSenderName = "Carlos Mendoza";
export const payoutClaimMockRecipientFirstName = "Maria";
export const payoutClaimMockRecipientLastName = "Lopez";

export const payoutClaimSendAmount = 100;
export const payoutClaimSourceCurrency = "USD";
export const payoutClaimDestinationCurrency: string = "MXN";
export const payoutClaimSourceCountry = "US";
export const payoutClaimDestinationCountry = "MX";

/** Receive-side estimate options (claimer picks payout). */
export const payoutClaimEstimateOptions: EstimateOption[] = [
  {
    capabilityKey: "US-MX-DEBIT_CARD-PUSH_TO_CARD",
    corridorCode: "US-MX",
    payerCode: null,
    fundingMethod: "DEBIT_CARD",
    payoutMethod: PayoutMethodType.PUSH_TO_CARD,
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 1,
    customerFxRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US:MX:CRB:CASH_PICKUP:JAL:GDL:ELEKTRA",
    corridorCode: "US-MX",
    payerCode: "ELEKTRA",
    fundingMethod: "DEBIT_CARD",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    sendAmount: 100,
    receiveAmount: 1720,
    customerFee: 1,
    customerFxRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US:MX:CRB:CASH_PICKUP:CMX:ANY:OXXO",
    corridorCode: "US-MX",
    payerCode: "OXXO",
    fundingMethod: "DEBIT_CARD",
    payoutMethod: PayoutMethodType.CASH_PICKUP,
    sendAmount: 100,
    receiveAmount: 1715,
    customerFee: 1,
    customerFxRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US-MX-DEBIT_CARD-BANK_ACCOUNT-BBVA",
    corridorCode: "US-MX",
    payerCode: "BBVA",
    fundingMethod: "DEBIT_CARD",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    sendAmount: 100,
    receiveAmount: 1742,
    customerFee: 1,
    customerFxRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  {
    capabilityKey: "US-MX-DEBIT_CARD-BANK_ACCOUNT-BANORTE",
    corridorCode: "US-MX",
    payerCode: "BANORTE",
    fundingMethod: "DEBIT_CARD",
    payoutMethod: PayoutMethodType.BANK_ACCOUNT,
    sendAmount: 100,
    receiveAmount: 1740,
    customerFee: 1,
    customerFxRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
];

/** Catalog banks offerable for BANK_ACCOUNT (gallery). */
export const payoutClaimBanks: Array<{
  id: string;
  name: string;
  accountPrefixes?: string[];
}> = [
  { id: "BBVA", name: "BBVA", accountPrefixes: ["012"] },
  { id: "BANORTE", name: "Banorte", accountPrefixes: ["072"] },
  { id: "SANTANDER", name: "Santander", accountPrefixes: ["014"] },
];

/** Saved destination bank accounts for the claimer (gallery). */
export const payoutClaimSavedBankAccounts: IBankAccountPaymentMethod[] = [
  {
    paymentMethodId: "pm_claim_ba_bbva",
    countryCode: "MX",
    bankName: "BBVA",
    maskedAccountNumber: "****9012",
    enabled: true,
    isDefault: true,
  },
];

export const payoutClaimDestinationCards: ICardPaymentMethod[] = [
  {
    paymentMethodId: "pm_claim_visa",
    brand: "visa",
    last4: "1111",
    expiryMonth: "09",
    expiryYear: "2029",
    cardholderName: "Maria Lopez",
    enabled: true,
    isDefault: true,
  },
];

/** Static cash habits shown above the claim cash pickup form (gallery only). */
export const payoutClaimCashPickupHabits: Array<{
  stateCode: string;
  stateName: string;
  payerId: string;
  payerDisplayName: string;
}> = [
  {
    stateCode: "JAL",
    stateName: "Jalisco",
    payerId: "ELEKTRA",
    payerDisplayName: "Elektra",
  },
  {
    stateCode: "CMX",
    stateName: "Ciudad de México",
    payerId: "OXXO",
    payerDisplayName: "Oxxo",
  },
];

export const payoutClaimCashStates: Array<{ code: string; name: string }> = [
  { code: "CMX", name: "Ciudad de México" },
  { code: "JAL", name: "Jalisco" },
  // NLE stays in the catalog so the picker can hide states with no priced SKU.
  { code: "NLE", name: "Nuevo León" },
];

export const payoutClaimCashPayers: Array<{ id: string; name: string }> = [
  { id: "ELEKTRA", name: "Elektra" },
  { id: "OXXO", name: "Oxxo" },
  { id: "BANCOPPEL", name: "BanCoppel" },
];

/** Receiver perspective: list shows receiveAmount + destinationCurrency. */
export const payoutClaimMockTransactions: ITransaction[] = [
  {
    operationId: "op_gallery_claim_completed",
    status: TransactionsStatus.COMPLETED,
    amount: "1,742.00",
    currency: "MXN",
    date: "10/07/26",
    recipientName: payoutClaimMockSenderName,
  },
  {
    operationId: "op_gallery_claim_processing",
    status: TransactionsStatus.PENDING,
    amount: "1,720.00",
    currency: "MXN",
    date: "08/07/26",
    recipientName: payoutClaimMockSenderName,
  },
];

export const DEFAULT_PAYOUT_CLAIM_RECEIPT_ID = "op_gallery_claim_completed";

export const payoutClaimMockReceipts: Record<string, FundRequestMockReceipt> = {
  op_gallery_claim_completed: {
    operationId: "op_gallery_claim_completed",
    status: "completed",
    createdAt: "2026-07-10T12:00:00.000Z",
    receiverName: `${payoutClaimMockRecipientFirstName} ${payoutClaimMockRecipientLastName}`,
    sendAmount: 100,
    receiveAmount: 1742,
    totalCost: 101,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
  op_gallery_claim_processing: {
    operationId: "op_gallery_claim_processing",
    status: "processing",
    createdAt: "2026-07-08T12:00:00.000Z",
    receiverName: `${payoutClaimMockRecipientFirstName} ${payoutClaimMockRecipientLastName}`,
    sendAmount: 100,
    receiveAmount: 1720,
    totalCost: 101,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: PAYOUT_CLAIM_MOCK_FX,
    sourceCurrency: "USD",
    destinationCurrency: "MXN",
  },
};

export const resolvePayoutClaimReceiptId = (
  paramId: string | undefined,
  pathname: string
): string => {
  if (paramId && payoutClaimMockReceipts[paramId]) return paramId;
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (lastSegment && payoutClaimMockReceipts[lastSegment]) return lastSegment;
  return DEFAULT_PAYOUT_CLAIM_RECEIPT_ID;
};

export const getPayoutClaimMockReceipt = (
  paramId: string | undefined,
  pathname: string
): FundRequestMockReceipt =>
  payoutClaimMockReceipts[resolvePayoutClaimReceiptId(paramId, pathname)] ??
  payoutClaimMockReceipts[DEFAULT_PAYOUT_CLAIM_RECEIPT_ID];
